/** Every decision is in `lib/password.ts` and tested there; this file only draws it. */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { api, errorMessage } from '../lib/api'
import { passwordProblem, passwordText, refusalText } from '../lib/password'
import { signOutEverywhere } from '../lib/teams'
import type { User } from '../lib/types'
import PasswordChecklist from './PasswordChecklist'
import { PasswordInput } from './PasswordInput'
import { btnGhost, btnPrimary, cardClass, Notice } from './ui'

type Person = Pick<User, 'email' | 'display_name' | 'username'>

function ChangePasswordForm({ person, onChanged }: { person: Person | null; onChanged?: () => void }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [refusal, setRefusal] = useState<string | null>(null)

  const change = useMutation({
    mutationFn: async () =>
      (await api.post('/api/auth/password/change', { current_password: current, new_password: next })).data,
    onSuccess: () => {
      toast.success(t('password.changed'))
      setCurrent('')
      setNext('')
      setConfirm('')
      setRefusal(null)
      void queryClient.invalidateQueries({ queryKey: ['me'] })
      onChanged?.()
    },
    onError: (error) => {
      const text = refusalText(error)
      setRefusal(text ? passwordText(t, text) : errorMessage(error))
    },
  })

  // Shown only once the person has typed into the new-password boxes, so an untouched form is not
  // already scolding them.
  const username = person?.username ?? null
  const context = { username, email: person?.email, names: [person?.display_name] }
  const problem = next || confirm ? passwordProblem(next, username, confirm, context) : null
  const canSubmit = Boolean(current) && Boolean(next) && passwordProblem(next, username, confirm, context) === null && !change.isPending

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (canSubmit) change.mutate()
      }}
      className="space-y-3"
    >
      <PasswordInput
        label={t('password.currentPassword')}
        required
        autoComplete="current-password"
        value={current}
        onChange={(e) => setCurrent(e.target.value)}
      />
      <PasswordInput
        label={t('password.newPassword')}
        required
        autoComplete="new-password"
        value={next}
        onChange={(e) => setNext(e.target.value)}
      />
      <PasswordInput
        label={t('password.confirmPassword')}
        required
        autoComplete="new-password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
      />
      <PasswordChecklist password={next} context={context} />
      {problem === 'mismatch' && <Notice tone="warning">{passwordText(t, problem)}</Notice>}
      {refusal && <Notice tone="danger">{refusal}</Notice>}
      <button type="submit" disabled={!canSubmit} className={`w-full ${btnPrimary}`}>
        {change.isPending ? t('password.changing') : t('password.change')}
      </button>
    </form>
  )
}

/** Nothing else of the app renders behind it, because every other route answers 403 until the password is changed. */
export function ForcedPasswordChange({ user }: { user: User }) {
  const { t } = useTranslation()
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 dark:bg-slate-900">
      <div className="w-full max-w-sm space-y-4 rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
        <div>
          <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100">{t('password.forcedTitle')}</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{user.display_name}</p>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{t('password.forcedIntro')}</p>
        </div>
        <ChangePasswordForm person={user} />
        <button
          type="button"
          onClick={() => {
            void signOutEverywhere().finally(() => window.location.reload())
          }}
          className={`w-full ${btnGhost}`}
        >
          {t('app.signOut')}
        </button>
      </div>
    </div>
  )
}

export function ChangePasswordCard({ user }: { user?: User | null }) {
  const { t } = useTranslation()
  return (
    <section className={`${cardClass} max-w-md space-y-3`}>
      <div>
        <h2 className="font-semibold text-slate-800 dark:text-slate-100">{t('password.cardTitle')}</h2>
        <p className="text-xs text-slate-500 dark:text-slate-400">{t('password.cardHint')}</p>
      </div>
      <ChangePasswordForm person={user ?? null} />
    </section>
  )
}
